import { useEffect, useState } from "react";
import { TeamSection } from "./settings/TeamSection";
import { StripeConnectSection } from "./settings/StripeConnectSection";
import { LandlordPropertySection } from "./settings/LandlordPropertySection";
import { CompanyProfileSection } from "./settings/CompanyProfileSection";
import { LateFeeSection } from "./settings/LateFeeSection";
import { PaymentSettingsSection } from "./settings/PaymentSettingsSection";
import { PlanBillingSection } from "./settings/PlanBillingSection";
import { TeamAddonCard } from "./settings/TeamAddonCard";
import { AccountManagementSection } from "./settings/AccountManagementSection";
import { PLAN_LABEL, PLAN_COLOR, PLAN_DESC } from "./settings/constants";
import { btnPrimary } from "./shared";

export default function Settings({
  page, can, subTab, session, setPage,
  settings, setSettings, settingsSaved, saveSettings,
  teamMembers, newMember, setNewMember,
  showAddMember, setShowAddMember,
  addTeamMember, resendInvite, removeTeamMember, updateMemberRole,
  companyProfile, setCompanyProfile, companyProfileSaved, logoSaved, saveCompanyProfile,
}) {
  const showTeam = page === "Team" || subTab === "Team";
  const tier      = session?.user?.user_metadata?.tier || "free";
  const isOwner   = tier === "owner";
  const planLabel = PLAN_LABEL[tier]  || "Free";
  const planColor = PLAN_COLOR[tier]  || "#9B9690";
  const planDesc  = PLAN_DESC[tier]   || PLAN_DESC.free;
  const isPaid    = tier !== "free";

  // Only the account owner can deactivate or delete — not team members.
  // Resolved server-side via team_members lookup (get-owner-context) instead
  // of user_metadata.landlord_id, which is client-writable. Fails closed
  // (hidden) until the check resolves.
  const [isAccountOwner, setIsAccountOwner] = useState(false);
  useEffect(() => {
    let cancelled = false;
    (async () => {
      const { supabase: sb } = await import("../supabaseClient");
      const { data, error } = await sb.functions.invoke("get-owner-context");
      if (!cancelled && !error) setIsAccountOwner(!data?.is_team_member);
    })();
    return () => { cancelled = true; };
  }, []);

  // ── Access guards ─────────────────────────────────────────────────────────────
  if (!showTeam && !can.viewSettings) {
    return (
      <div style={{ padding: 60, textAlign: "center", color: "#9B9690" }}>
        <div style={{ fontSize: 40, marginBottom: 16 }}>🔒</div>
        <div style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: 22, color: "#163C2F", marginBottom: 8 }}>Access Restricted</div>
        <div style={{ fontSize: 14 }}>Settings are only accessible to admins.</div>
      </div>
    );
  }
  if (showTeam && !can.manageTeam) {
    return (
      <div style={{ padding: 60, textAlign: "center", color: "#9B9690" }}>
        <div style={{ fontSize: 40, marginBottom: 16 }}>🔒</div>
        <div style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: 22, color: "#163C2F", marginBottom: 8 }}>Access Restricted</div>
        <div style={{ fontSize: 14 }}>Team management is only accessible to admins.</div>
      </div>
    );
  }

  // ── Team tab ──────────────────────────────────────────────────────────────────
  if (showTeam) {
    return (
      <TeamSection
        teamMembers={teamMembers}
        isOwner={isOwner}
        isPaid={isPaid}
        companyProfile={companyProfile}
        newMember={newMember}
        setNewMember={setNewMember}
        showAddMember={showAddMember}
        setShowAddMember={setShowAddMember}
        addTeamMember={addTeamMember}
        resendInvite={resendInvite}
        removeTeamMember={removeTeamMember}
        updateMemberRole={updateMemberRole}
      />
    );
  }

  // ── Profile tab ───────────────────────────────────────────────────────────────
  return (
    <>
      <div style={{ marginBottom: 28 }}>
        <h1 style={{ fontFamily: "'Cormorant Garamond', serif", fontWeight: 600, fontSize: 32, color: "#2B2B2B", margin: 0 }}>Settings</h1>
        <div style={{ color: "#9B9690", marginTop: 4, fontSize: 14 }}>Company profile, landlord info &amp; messaging credentials</div>
      </div>

      <StripeConnectSection companyProfile={companyProfile} />

      <LandlordPropertySection settings={settings} setSettings={setSettings} saveSettings={saveSettings} />

      <CompanyProfileSection
        companyProfile={companyProfile}
        setCompanyProfile={setCompanyProfile}
        companyProfileSaved={companyProfileSaved}
        logoSaved={logoSaved}
        saveCompanyProfile={saveCompanyProfile}
      />

      <LateFeeSection settings={settings} setSettings={setSettings} />

      <PaymentSettingsSection settings={settings} setSettings={setSettings} setPage={setPage} />

      <div style={{ marginBottom: 20, fontSize: 12, color: "#9B9690", fontFamily: "'Manrope', sans-serif" }}>
        Still have questions?{" "}
        <a href="mailto:support@leaslypro.com" style={{ color: "#214D3A", fontWeight: 600, textDecoration: "underline" }}>
          Email us at support@leaslypro.com
        </a>
      </div>

      <div style={{ marginTop: 0, marginBottom: 20, display: "flex", gap: 12, alignItems: "center" }}>
        <button style={btnPrimary} onClick={saveSettings}>Save Settings</button>
        {settingsSaved && <span style={{ color: "#22c55e", fontSize: 13, fontWeight: 600 }}>✅ Saved!</span>}
      </div>

      <PlanBillingSection
        tier={tier}
        isOwner={isOwner}
        isPaid={isPaid}
        planLabel={planLabel}
        planColor={planColor}
        planDesc={planDesc}
        companyProfile={companyProfile}
      />

      <TeamAddonCard
        tier={tier}
        isOwner={isOwner}
        isPaid={isPaid}
        teamAddon={companyProfile?.team_add_on || "none"}
        teamMembers={teamMembers}
      />

      {isAccountOwner && <AccountManagementSection />}
    </>
  );
}
